import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { Button, Scroll, Popover, Checkbox, Portal, Tooltip, Popup } from 'next-common-lib';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getGroupsAPI, createGroupAPI, createContactAPI, getContactAPI, updateContactAPI } from '../../apis/contactAPI';
import { getUserInfoAPI } from '../../apis/userApi';
import useClickOutsideGrouping from '../../hooks/useClickOutsideGrouping';
import classes from './ContactGroupSelect.module.scss';
import i18n from '../i18n/i18n';

const MAX_LENGTH_BYTE = 90;
const POSITION = {
  TOP: 'top',
  LEFT: 'left',
  RIGHT: 'right',
  BOTTOM: 'bottom',
};

const MAX_GROUP_CONTACT_MEM = 500;

// eslint-disable-next-line import/prefer-default-export
export const ContactGroupSelect = forwardRef((props, ref) => {
  const {
    children,
    dataSource,
    onApply,
    checkedGroups,
    className,
    zIndex,
    positionX,
    positionY,
    isShow,
    canClickChildren,
    close,
    isGetSelectedGroups,
    getSelectedGroups,
    isCreateContact,
    isCopyContact,
    space,
    afterCreateGroup,
  } = props;

  const [groups, setGroups] = useState([]);
  const [isShowInput, setIsShowInput] = useState(false);
  const [subject, setSubject] = useState('');
  const [isShowFeedback, setIsShowFeedback] = useState(false);
  const [isShowAdded, setIsShowAdded] = useState(false);
  const [isShowConfirmCopy, setIsShowConfirmCopy] = useState(false);
  const [message, setMessage] = useState('');

  const childrenWrapRef = ref || useRef(null);
  const popoverRef = useRef(null);
  const popoverFeedbackRef = useRef(null);
  const [isChildrenShow, setIsChildrenShow] = useState(false);
  const [userInfoLogin, setUserInfoLogin] = useState();

  const fetchUserInfo = async () => {
    const [userInfo] = await Promise.all([getUserInfoAPI()]);
    setUserInfoLogin(userInfo);
  };

  // Fetch group list
  const fetchGroups = async () => {
    try {
      const data = await getGroupsAPI(0, 1000);
      if (data && data.elements && data.elements.length > 0) {
        const list = data.elements.map(item => {
          let selected = false;
          checkedGroups &&
            checkedGroups.find(name => {
              if (item?.subject && item?.subject === name) {
                selected = true;
              }
            });
          return {
            ...item,
            selected,
          };
        });
        setGroups(list);
      } else {
        setGroups([]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleCheck = index => {
    groups[index].selected = !groups[index].selected;
  };

  const handleCreateGroup = async () => {
    try {
      if (subject.trim()) {
        const isDuplicate = groups.find(item => {
          if (item.subject === subject.trim()) {
            setMessage(i18n.t('contacts.common.message.duplicatedGroup', { ns: 'contacts' }));
            setIsShowFeedback(true);
          }
        });
        if (!isDuplicate) {
          const data = await createGroupAPI(subject.trim());
          if (data) {
            fetchGroups();
            setSubject('');
            setIsShowInput(false);
            afterCreateGroup();
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleApply = async () => {
    // Get group selected
    const groupsSelected = groups.filter(item => item.selected) || [];

    const isMaxMember = groupsSelected.some(gr => gr.memberNum === MAX_GROUP_CONTACT_MEM);
    if (isMaxMember && !isGetSelectedGroups) {
      setMessage(i18n.t('contacts.group.message.exceedNumber', { ns: 'contacts' }));
      setIsShowFeedback(true);
      setIsChildrenShow(false);
      close();
      return;
    }

    // List subject groups 's selected
    const list = groupsSelected.map(item => {
      return item.subject;
    });

    let typeContactData = dataSource
      .filter(data => data.type === 'CONTACT')
      .map(item => {
        return {
          ...item,
          groupNames: list, // Update name groups of datasource
        };
      });

    if (isCopyContact) {
      typeContactData = dataSource
        .filter(data => 'EMP')
        .map(item => {
          return {
            ...item,
            groupNames: list, // Update name groups of datasource
            type: 'CONTACT',
          };
        });
    }

    const typeEMPData = dataSource
      .filter(data => data.type === 'EMP')
      .map(item => {
        return {
          ...item,
        };
      });

    if (isGetSelectedGroups) {
      getSelectedGroups(list);
      setIsChildrenShow(false);
      return;
    }
    // If no contact is pass from prop
    if (dataSource && dataSource.length === 0) {
      setMessage(i18n.t('contacts.common.message.selectGroupMember', { ns: 'contacts' }));
      setIsShowFeedback(true);
      setIsChildrenShow(false);
      return;
    }
    if (dataSource && dataSource.length > 0) {
      try {
        if (isCopyContact) {
          typeContactData = await createContactAPI(typeContactData, dataSource[0].type === 'EMP');
          if (typeContactData) {
            setMessage(i18n.t('contacts.common.message.copyContact', { ns: 'contacts' }));
          }
        } else {
          if (isCreateContact) typeContactData = await createContactAPI(typeContactData);
          const listResults = await Promise.all(
            groupsSelected.map(async item => {
              const data = await getContactAPI(item.uid);
              data.contacts = data.contacts
                ? [...data.contacts, ...typeContactData, ...typeEMPData]
                : [...typeContactData, ...typeEMPData]; // If exist contacts, update with news
              const success = await updateContactAPI(item.uid, data);
              if (success) {
                return true;
              }
              return false;
            }),
          );
          if (!listResults.includes(false)) {
            setMessage(i18n.t('contacts.popup.message.addedToContacts', { ns: 'contacts' }));
          }
          getSelectedGroups(list);
        }
        setIsShowAdded(true);
        setTimeout(() => {
          setIsShowAdded(false);
        }, 2000);
        setIsChildrenShow(false);
        close();
      } catch (e) {
        setMessage(e);
        setIsShowFeedback(true);
      }
    }
    onApply();
  };

  const _onClick = () => {
    setIsChildrenShow(prev => !prev);
  };

  const setTopPosition = () => {
    let position = 0;
    switch (positionY) {
      case POSITION.TOP:
        position = childrenWrapRef.current?.getBoundingClientRect().top - 315 - space;
        break;
      case POSITION.BOTTOM:
        position = childrenWrapRef.current?.getBoundingClientRect().bottom + space;
        break;
      default:
        position = childrenWrapRef.current?.getBoundingClientRect().top;
        break;
    }
    if (position < 0) {
      return window.pageYOffset + childrenWrapRef.current?.getBoundingClientRect().bottom + space; // out top
    }
    if (position > window.innerHeight - 315 - space) {
      return window.pageYOffset + childrenWrapRef.current?.getBoundingClientRect().top - 315 - space; // out bottom
    }
    return position + window.pageYOffset;
  };

  const setLeftPosition = () => {
    let position = 0;
    switch (positionX) {
      case POSITION.LEFT:
        position = childrenWrapRef.current?.getBoundingClientRect().left - 200 - space;
        break;
      case POSITION.RIGHT:
        position = childrenWrapRef.current?.getBoundingClientRect().right + space;
        break;
      default:
        position = childrenWrapRef.current?.getBoundingClientRect().left;
        break;
    }
    if (position < 0) {
      return window.pageXOffset + childrenWrapRef.current?.getBoundingClientRect().left + space; // out left
    }
    if (position > window.innerWidth - 200 - space) {
      return window.pageXOffset + childrenWrapRef.current?.getBoundingClientRect().right - 200 - space; // out right
    }
    return position + window.pageXOffset;
  };

  useEffect(() => {
    let element;
    if (canClickChildren && childrenWrapRef && childrenWrapRef.current) {
      element = childrenWrapRef.current;
      element.addEventListener('click', _onClick);
    }
    return () => {
      if (canClickChildren && childrenWrapRef && childrenWrapRef.current) {
        element = childrenWrapRef.current;
        element.removeEventListener('click', _onClick);
      }
    };
  }, [canClickChildren]);

  useEffect(() => {
    setIsChildrenShow(isShow);
  }, [isShow]);

  const popupFeedback = (
    <Popup
      ref={popoverFeedbackRef}
      closeFunction={() => {
        setIsShowFeedback(false);
      }}
      buttonComponent={[
        <div className="popover-footer">
          <div className="footer-container">
            <div className="btn-set al-right">
              <div className="btn-group">
                <Button
                  componentType="solid"
                  className="primary"
                  onClick={() => {
                    setIsShowFeedback(false);
                  }}
                  label={i18n.t('contacts.searchPeopleContactsTab.text.ok', { ns: 'contacts' })}
                />
              </div>
            </div>
          </div>
        </div>,
      ]}
      messageComponent={[
        <p key="1" className="pims-contact-popup-msg" style={{ wordBreak: 'keep-all' }}>
          {message}
        </p>,
      ]}
      size="sm"
      zIndex={zIndex + 1}
      titleComponent={[<h1 className="popup-title">{i18n.t('contacts.common.text.notice', { ns: 'contacts' })}</h1>]}
      theme={userInfoLogin?.DARKMODEYN === 'Y' ? 'dark' : 'light'}
    />
  );

  const popupConfirmCopy = (
    <Popover
      closeFunction={() => {
        setIsShowConfirmCopy(false);
      }}
      theme={userInfoLogin?.DARKMODEYN === 'Y' ? 'dark' : 'light'}
      bottomComponent={[
        <div className="popover-footer">
          <div className="footer-container">
            <div className="btn-set al-right">
              <div className="btn-group">
                <Button
                  componentType="solid"
                  className="secondary"
                  label={i18n.t('contacts.common.text.cancel', { ns: 'contacts' })}
                  onClick={() => {
                    setIsShowConfirmCopy(false);
                  }}
                />
                <Button
                  componentType="solid"
                  className="primary"
                  label={i18n.t('contacts.searchPeopleContactsTab.text.ok', { ns: 'contacts' })}
                  onClick={() => {
                    handleApply();
                    onApply();
                    setIsShowConfirmCopy(false);
                  }}
                />
              </div>
            </div>
          </div>
        </div>,
      ]}
      contentsComponent={[<p>{i18n.t('contacts.common.message.isThisContactCopy', { ns: 'contacts' })}</p>]}
      size="free"
      headerComponent={[
        <h1 className="popup-title">{i18n.t('contacts.common.button.copyContact', { ns: 'contacts' })}</h1>,
      ]}
    />
  );

  const uniqueId = () => parseInt(Date.now() * Math.random()).toString();

  const showToast = () => {
    Portal.open('snackbar', 'pims-contact-snackbar', { isShow: true, label: message });
  };

  useEffect(() => {
    if (isChildrenShow) {
      fetchGroups();
      fetchUserInfo();
    }
  }, [isChildrenShow]);

  useClickOutsideGrouping([popoverRef, childrenWrapRef, popoverFeedbackRef], () => {
    setSubject('');
    setIsShowInput(false);
    setIsChildrenShow(false);
    close();
  });

  const submitCopyContact = () => {
    const groupsSelected = groups.filter(item => item.selected);

    if (isCopyContact) {
      if (groupsSelected.length === 0) {
        setMessage(i18n.t('contacts.common.message.selectContGroup', { ns: 'contacts' }));
        setIsShowFeedback(true);
        setIsChildrenShow(false);
        close();
        return;
      }
      setIsShowConfirmCopy(true);
      setIsChildrenShow(false);
      return;
    }

    handleApply();
  };

  return (
    <>
      {React.cloneElement(children, { ref: childrenWrapRef })}
      <Popover
        theme={userInfoLogin?.DARKMODEYN === 'Y' ? 'dark' : 'light'}
        top={setTopPosition()}
        left={setLeftPosition()}
        zIndex={zIndex}
        ref={popoverRef}
        isShow={isChildrenShow}
        className={classNames('popover-container popover--flex-small', classes.popupContactSelect, className)}
        contentsComponent={[
          <>
            <div className={classNames('popover-list list--contact scroll-show', classes.listcontact)}>
              <div
                className="group-select"
                style={{ height: '100%', width: '100%', maxWidth: 'inherit', maxHeight: 'inherit', overflowY: 'auto' }}
              >
                <ul style={{ marginRight: '14px' }}>
                  {groups &&
                    groups.length > 0 &&
                    groups.map((item, index) => {
                      return (
                        <li key={`item-${index}`} className="popover-item category-item">
                          <Checkbox
                            checked={item.selected}
                            componentType="circle"
                            id={uniqueId()}
                            label={
                              <Tooltip componentType="move" placement="bottom" label={item.subject?.trim()}>
                                <span className="ellipsis">{item.subject?.trim()}</span>
                              </Tooltip>
                            }
                            onClick={() => {
                              handleCheck(index);
                            }}
                          />
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
            <div className="popover-list">
              <ul>
                {!isShowInput && (
                  <li className="popover-item">
                    <Button
                      label={i18n.t('contacts.common.text.createNewGroup', { ns: 'contacts' })}
                      className="pt-btn md text"
                      iconInfo={{ className: 'ic-plus', size: 'xs' }}
                      onClick={e => {
                        e.stopPropagation();
                        setIsShowInput(true);
                      }}
                    />
                  </li>
                )}
                {isShowInput && (
                  <li className="popover-item">
                    <div className={classNames('input-group', classes.wFull)}>
                      <input
                        maxLength={MAX_LENGTH_BYTE}
                        style={{ width: '30px' }}
                        type="text"
                        value={subject}
                        onKeyUp={e => {
                          e.preventDefault();
                          if (e.key === 'Enter') {
                            handleCreateGroup();
                          }
                        }}
                        onChange={e => {
                          const value = e.target.value;
                          setSubject(value);
                        }}
                      />
                      <Button
                        className="circle btn-tab-close"
                        componentType="icon"
                        iconInfo={{ className: 'ic-close', disabled: false, selected: false, size: 'xs' }}
                        onClick={e => {
                          setSubject('');
                        }}
                        size="xxs"
                      />
                      <Button
                        className="pt-btn primary"
                        componentType="button"
                        onClick={handleCreateGroup}
                        size="xs"
                        disabled={subject.trim().length === 0}
                        label={i18n.t('contacts.main.button.add', { ns: 'contacts' })}
                      />
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </>,
        ]}
        bottomComponent={[
          <div className="popover-footer">
            <div className="footer-container">
              <div className="btn-set al-right">
                <div className="btn-group">
                  <Button
                    componentType="solid"
                    className="secondary"
                    label={i18n.t('contacts.common.text.cancel', { ns: 'contacts' })}
                    onClick={() => {
                      setIsChildrenShow(false);
                      close();
                    }}
                  />
                  <Button
                    componentType="solid"
                    className="primary"
                    label={i18n.t('contacts.common.text.apply', { ns: 'contacts' })}
                    onClick={submitCopyContact}
                  />
                </div>
              </div>
            </div>
          </div>,
        ]}
        size="free"
      />
      {isShowAdded && showToast()}
      {isShowFeedback && popupFeedback}
      {isShowConfirmCopy && popupConfirmCopy}
    </>
  );
});

ContactGroupSelect.propTypes = {
  dataSource: PropTypes.arrayOf(Object),
  onApply: PropTypes.func,
  checkedGroups: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  zIndex: PropTypes.number,
  positionX: PropTypes.string,
  positionY: PropTypes.string,
  isShow: PropTypes.bool,
  children: PropTypes.node.isRequired,
  canClickChildren: PropTypes.bool,
  close: PropTypes.func,
  isCreateContact: PropTypes.bool,
  isCopyContact: PropTypes.bool,
  isGetSelectedGroups: PropTypes.bool,
  space: PropTypes.number,
  afterCreateGroup: PropTypes.func,
  getSelectedGroups: PropTypes.func,
};

ContactGroupSelect.defaultProps = {
  dataSource: [],
  onApply: () => {},
  checkedGroups: [],
  className: '',
  zIndex: 2000,
  positionX: '',
  positionY: '',
  isShow: false,
  canClickChildren: true,
  close: () => {},
  afterCreateGroup: () => {},
  getSelectedGroups: () => {},
  isGetSelectedGroups: false,
  isCreateContact: false,
  isCopyContact: false,
  space: 0,
};
